import { Data, Modal, Print, Utils } from 'common';
import includes from 'lodash/includes';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import React, { useContext, useEffect } from 'react';
import HistoryCommunication from '../utils/HistoryCommunication';

const {
	PrintOnEnum,
	PrintOnOptions,
	PrintCategory,
	LabelPreviewHelper,
	PrintTypeHelper,
	PrintLayout,
	Carrier,
	CalculatedData,
} = Data;
const { PrintModal } = Print;
const { Session, PrintUtil, Analytics } = Utils;
const { ModalContext } = Modal;

const ReprintType = Object.freeze({
	NetStamps: 'NetStamps',
	Label: 'Label',
	Envelope: 'Envelope',
	CertifiedMail: 'CertifiedMail',
});

const ReprintModal = ({
	modalId,
	data: { labelDetails, selectedPrint, setLabelDataForPrint, callback },
}) => {
	const { showError } = useContext(ModalContext);
	const { labelId, requestPrintLayout } = labelDetails;
	const {
		printDetails: { netstampCount, carrier },
	} = selectedPrint;

	useEffect(() => {
		if (isFunction(callback)) {
			callback();
		}
	}, []);

	const getSheetDimensions = (sheet, series) => {
		const { numCols, numRows } = LabelPreviewHelper.getData(sheet, series);

		return numCols * numRows;
	};

	// check if the netstamp layout type is valid for the requested reprint
	// netstamp rolls can only be printed on rolls, while netstamp sheets are interchangeable amongst each other
	const isNetStampsLayoutTypeValid = (sheet, series) => {
		const sheetData = LabelPreviewHelper.getData(sheet, series);
		const nsLayout = isString(sheetData?.type)
			? PrintTypeHelper.getSwsimNetStampLayout(sheetData.type)
			: '';
		const requestedReprintLayout = labelDetails?.requestPrintLayout;

		if (
			requestedReprintLayout?.includes(PrintLayout.RollTypeSorD) &&
			nsLayout !== PrintLayout.RollTypeSorD
		) {
			return {
				isValid: false,
				errorMessage: `Your original NetStamps print was on a roll. Please enter a serial number for a NetStamps roll that can fit all ${netstampCount} NetStamp${
					netstampCount > 1 ? 's' : ''
				} from the original print.`,
			};
		} else if (
			!requestedReprintLayout?.includes(PrintLayout.RollTypeSorD) &&
			nsLayout === PrintLayout.RollTypeSorD
		) {
			return {
				isValid: false,
				errorMessage: `Your original NetStamps print was on a sheet. Please enter a serial number for a NetStamps sheet that can fit all ${netstampCount} NetStamp${
					netstampCount > 1 ? 's' : ''
				} from the original print.`,
			};
		}

		return { isValid: true };
	};

	// check if the netstamp series fits the total netstamp count on the sheet, or if the sheet is reprintable
	const isSeriesValid = (sheet, series) => {
		const sheetDimensions = getSheetDimensions(sheet, series);

		if (netstampCount > sheetDimensions) {
			return {
				isValid: false,
				errorMessage: `Please enter a serial number for a NetStamps sheet that can fit all ${netstampCount} NetStamp${
					netstampCount > 1 ? 's' : ''
				} from the original print.`,
			};
		}

		return { isValid: true };
	};

	// check if the user selected starting position fits the total netstamp count on the sheet
	const isPositionValid = (startPos, qty, sheet, series) => {
		const sheetDimensions = getSheetDimensions(sheet, series);

		if (startPos + qty - 1 > sheetDimensions) {
			return {
				isValid: false,
				errorMessage: `Please select a starting slot for your reprint that can accommodate ${qty} NetStamp${
					qty > 1 ? 's' : ''
				}.`,
			};
		}

		return { isValid: true };
	};

	const getReprintCategory = () => {
		if (PrintCategory.NetStamps.includes(requestPrintLayout)) {
			return ReprintType.NetStamps;
		} else if (PrintCategory.Envelope.includes(requestPrintLayout)) {
			return ReprintType.Envelope;
		} else if (PrintCategory.CertifiedMail.includes(requestPrintLayout)) {
			return ReprintType.CertifiedMail;
		}

		return ReprintType.Label;
	};

	const getSelectedOption = () => {
		const reprintCategory = getReprintCategory();

		if (reprintCategory === ReprintType.NetStamps) {
			return PrintOnEnum.NetStamps;
		} else if (reprintCategory === ReprintType.Envelope) {
			return PrintOnEnum.EnvelopeA2;
		} else if (reprintCategory === ReprintType.CertifiedMail) {
			// Certified Mail reprints aren't interchangeable
			return PrintTypeHelper.getCertifiedMailPrintOn(requestPrintLayout);
		} else if (reprintCategory === ReprintType.Label) {
			if (
				requestPrintLayout?.includes('4X6') &&
				(carrier === Carrier.UPS ||
					carrier === Carrier.FedEx ||
					carrier === Carrier.DHLExpress)
			) {
				return PrintOnEnum.Roll4x6;
			}
			return PrintOnEnum.ShippingLabel812x11;
		}
	};

	const getFilteredPrintOnOptions = () => {
		const { IsInWinClient } = Session.getSettings();
		const reprintCategory = getReprintCategory();
		let filteredPrintOnOptions = PrintOnOptions;

		// WinClient does not support Connect printing, so prints that require Connect must be filtered out
		if (IsInWinClient) {
			// filter out rolls if not eligible for PDF (check labelFormat)
			filteredPrintOnOptions = PrintOnOptions.filter(({ value }) => {
				const isRoll = value.includes('Roll');
				const isPdfRequired = PrintUtil.isEligibleForPdfPrint(
					[selectedPrint],
					value
				);

				return !(isRoll && isPdfRequired);
			});
		}

		if (reprintCategory === ReprintType.NetStamps) {
			return filteredPrintOnOptions.filter(
				({ value }) => value === PrintOnEnum.NetStamps
			);
		} else if (reprintCategory === ReprintType.Envelope) {
			return filteredPrintOnOptions.filter(({ value }) =>
				PrintTypeHelper.isEnvelopePrint(value)
			);
		} else if (reprintCategory === ReprintType.CertifiedMail) {
			// return a PrintOnEnum that matches the requestPrintLayout
			return filteredPrintOnOptions.filter(
				({ value }) =>
					value === PrintTypeHelper.getCertifiedMailPrintOn(requestPrintLayout)
			);
		} else if (reprintCategory === ReprintType.Label) {
			return filteredPrintOnOptions.filter(({ value }) => {
				// these carrier labels are generated by ShipEngine, which cannot be modified when reprinting.
				// therefore, rolls must be reprinted on rolls, and sheets on sheets.
				if (
					carrier === Carrier.UPS ||
					carrier === Carrier.FedEx ||
					carrier === Carrier.DHLExpress
				) {
					if (requestPrintLayout?.includes('4X6')) {
						return [PrintOnEnum.Roll4x6, PrintOnEnum.Roll418x614].includes(
							value
						);
					} else {
						return [
							PrintOnEnum.ShippingLabel812x11,
							PrintOnEnum.ShippingLabel1200,
							PrintOnEnum.ShippingLabel512x812,
						].includes(value);
					}
				}

				// letter size labels can't be reprinted on 4x6
				if (PrintTypeHelper.isLabelLetterSize(requestPrintLayout)) {
					return [
						PrintOnEnum.ShippingLabel812x11,
						PrintOnEnum.ShippingLabel1200,
						PrintOnEnum.ShippingLabel512x812,
					].includes(value);
				}

				return [
					PrintOnEnum.ShippingLabel812x11,
					PrintOnEnum.ShippingLabel1200,
					PrintOnEnum.ShippingLabel512x812,
					PrintOnEnum.Roll4x6,
					PrintOnEnum.Roll418x614,
				].includes(value);
			});
		}

		return filteredPrintOnOptions;
	};

	const calculateStartPosition = (numCols, startPosition) => {
		// calculate starting row
		const startRow = Math.ceil(startPosition / numCols);

		// calculate starting column
		let startColumn = startPosition % numCols;
		if (startColumn === 0) {
			startColumn = numCols;
		}

		// Return starting row and starting column
		return { startRow, startColumn };
	};

	const getPrintTypeForLogging = reprintData => {
		if (PrintUtil.isPluginPrintFlow(reprintData)) {
			return 'plugin';
		} else if (reprintData?.labelFormat === 'pdf') {
			return 'pdf';
		} else {
			return 'gif';
		}
	};

	const segmentTrackingForReprint = (reprintData = {}) => {
		const { printOn, series } = reprintData;
		const {
			fromAddress: { postalCode },
			toAddress: { countryCode },
			weightOunces,
			weightPounds,
			carrierName,
			serviceType,
			packagingType,
			insuredValue,
			deliveryConfirmationTypeName,
			orderId,
			shipDate,
		} = selectedPrint?.printDetails || {};
		const {
			package: { length, height, width } = {},
			customs,
			declaredValue,
		} = labelDetails;
		const insureForCheckbox = insuredValue > 0;
		const weight = weightPounds * 16 + weightOunces;
		const carrierIdMap = {
			None: 0,
			USPS: 1,
			UPS: 3,
			GlobalPost: 60,
			DHLExpress: 38,
			FedEx: 4,
		};
		const carrierId = carrierIdMap[carrierName];
		const service = CalculatedData.getService(
			serviceType,
			packagingType
		)?.serviceName;
		const segmentData = {
			FromZip: postalCode,
			ToCountryName: countryCode,
			Weight: weight,
			Length: length,
			Width: width,
			Height: height,
			CarrierID: carrierId,
			Service: service,
			PackageType: packagingType,
			TotalShipCost: selectedPrint?.totalAmount,
			InsureForCheckox: insureForCheckbox,
			Tracking: deliveryConfirmationTypeName,
			OrderId: orderId,
			PrintingOn: printOn,
			PrintType: getPrintTypeForLogging(reprintData),
			ShipDate: shipDate,
		};

		if (printOn === PrintOnEnum.NetStamps) {
			segmentData.StampQuantity = netstampCount;
			segmentData.SerialNumber = series;
		}

		if (customs) {
			segmentData.CustomsPackageContents = customs?.contentsType;
			segmentData.CustomsNonDeliveryOption = customs?.nonDeliveryOption;
			segmentData.CustomsMoreInfo = customs?.contentsDescription;
			segmentData.CustomsItemsCount = customs?.customsItems?.length;
			segmentData.CustomsTotalValue =
				customs?.customsItems?.reduce(
					(total, item) =>
						total + (item.quantity || 0) * (item.unitValue?.amount || 0),
					0
				) ?? 0;
		}

		if (carrier === Carrier.UPS) {
			segmentData.DeclaredValue = declaredValue;
		} else {
			segmentData.InsureForAmount = insuredValue;
		}

		Analytics.trackEvent(
			'reprint_from_history',
			segmentData,
			Analytics.agent.segment
		);
	};

	const onReprintClick = async (
		startPosition,
		printOn,
		sheetData,
		series,
		sheet
	) => {
		const { numCols } = sheetData;
		const reprintData = {
			labelId,
			labelOutputType: 'url',
			labelFormat: 'png',
			printOn,
			sheet,
			series,
			printLayout: requestPrintLayout,
		};
		const isThermal = reprintData.printOn?.includes('roll');
		let response = {};

		if (printOn === PrintOnEnum.NetStamps) {
			const { startRow, startColumn } = calculateStartPosition(
				numCols,
				startPosition
			);

			reprintData.labelFormat = 'png';
			reprintData.startRow = startRow;
			reprintData.startColumn = startColumn;
			reprintData.serialNumber = series;

			response = await HistoryCommunication.getNetstampsReprint(reprintData);
		} else if (PrintTypeHelper.isEnvelopePrint(printOn)) {
			reprintData.envelopeType = PrintTypeHelper.getSeraEnvelopeType(printOn);

			response = await HistoryCommunication.getEnvelopeReprint(reprintData);
		} else {
			reprintData.labelSize = PrintTypeHelper.isLabelLetterSize(
				requestPrintLayout
			)
				? 'letter'
				: '4x6';
			reprintData.labelFormat = PrintUtil.isEligibleForPdfPrint(
				[selectedPrint],
				printOn,
				reprintData
			)
				? 'pdf'
				: 'png';

			response = await HistoryCommunication.getLabelReprint(reprintData);
		}

		if (HistoryCommunication.isSuccessful(response)) {
			const labelUrls = response?.data?.labels?.map(({ href }) => href);
			const formUrls = response?.data?.forms?.map(({ href }) => href);
			const labelData = {
				labelUrls,
				formUrls,
				layoutConfig: {
					...reprintData,
					isThermal,
				},
				labelDetails,
			};

			setLabelDataForPrint(labelData);
		} else if (includes(response?.ErrorDescription, 'Invalid label_id')) {
			// the label isn't known to the web client, e.g. it was printed with the Desktop Application
			showError({
				data: {
					handleType: 'custom',
					code: response.ErrorCode,
					title: 'Unable to Reprint',
					description:
						'<p>Unable to reprint this label. If the label was originally printed using the Classic Stamps.com Desktop application, please use the same application to reprint it.</p>',
				},
			});
		} else {
			showError({ response });
		}

		segmentTrackingForReprint({ ...reprintData, isThermal });
	};

	const getHeaderText = () => {
		let headerText = 'label is';

		if (getReprintCategory() === 'NetStamps') {
			headerText = 'NetStamps are';
		}

		return `Your ${headerText} ready to reprint`;
	};

	return (
		<>
			<PrintModal
				modalId={modalId}
				data={{
					header: getHeaderText(),
					isReprint: true,
					totalCost: 0,
					selectedPrint,
					onReprintClick,
					filteredPrintOnOptions: getFilteredPrintOnOptions(),
					selectedOption: getSelectedOption(),
					isSeriesValid,
					isPositionValid,
					isNetStampsLayoutTypeValid,
					netstampCount,
					carrier,
				}}
			/>
		</>
	);
};

export default ReprintModal;
